import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "../../../../../enums/reducer";
import { type RequestState, RStatus } from "../../../../Application/globaltypes/fetchRequest";
import { type RootState } from "../../../../Application/globaltypes/redux";
import {
  type SurveyPerformanceAnswer,
  type SurveyPeopleDetails,
  type SurveyQuestion,
  SurveyQuestionType,
} from "../../types/state";
import { type Content } from "../../types/models";
import { completes, starts } from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { cloneDeep } from "lodash";
import moment from "moment";

const namespace = ReducerNamespaceTypes.Survey;
const entityPrefix = ReducerEntityPrefixTypes.Surveys_Performance;

export type EngagementLineData = {
  Date: string;
  Events: {
    CompletedCount: number;
    CumulativeCompletedCount: number;
    CumulativeStartedCount: number;
    StartedCount: number;
  };
}[];

export type EngagementData = {
  AverageTime: number;
  CompletionRate: number;
  Completions: number;
  Customers: number;
  Started: number;
};

const initialRequestState: RequestState<any> = {
  status: RStatus.Idle,
  value: [],
  errorMessage: undefined,
};

export interface SurveyPerformanceState {
  engagementLineData: RequestState<EngagementLineData>;
  surveyQuestionsData: RequestState<SurveyQuestion[]>;
  surveySelectedQuestionData: RequestState<SurveyQuestion>;
  surveySelectedAnswerData: RequestState<SurveyPerformanceAnswer>;
  surveyContentData: Content;
  engagementData: RequestState<EngagementData>;
  surveyPeopleDetails: RequestState<SurveyPeopleDetails[]>;
}

export const initialState: SurveyPerformanceState = {
  surveyQuestionsData: initialRequestState,
  engagementLineData: initialRequestState,
  surveySelectedQuestionData: initialRequestState,
  surveySelectedAnswerData: initialRequestState,
  surveyContentData: {
    surveyId: -1,
    questions: [],
    dateModified: "",
    title: "",
    isDraft: false,
  },
  engagementData: initialRequestState,
  surveyPeopleDetails: initialRequestState,
};

const surveyPerformanceSlice = createSlice({
  name: `${namespace}/${entityPrefix}`,
  initialState,
  reducers: {
    reqSurveyQuestionsData(state: SurveyPerformanceState) {
      state.surveyQuestionsData = {
        ...state.surveyQuestionsData,
        status: RStatus.Pending,
      };
    },
    reqEngagementLineData(state: SurveyPerformanceState) {
      state.engagementLineData = {
        ...state.engagementLineData,
        status: RStatus.Pending,
      };
    },
    reqSelectedQuestionData(state: SurveyPerformanceState) {
      state.surveySelectedQuestionData = {
        ...state.surveySelectedQuestionData,
        status: RStatus.Pending,
      };
    },
    reqSelectedAnswerData(state: SurveyPerformanceState) {
      state.surveySelectedAnswerData = {
        ...state.surveySelectedAnswerData,
        status: RStatus.Pending,
      };
    },
    reqPeopleDetails(state: SurveyPerformanceState) {
      state.surveyPeopleDetails = {
        ...state.surveyPeopleDetails,
        status: RStatus.Pending,
      };
    },
    reqEngagementData(state: SurveyPerformanceState) {
      state.engagementData = {
        ...state.engagementData,
        status: RStatus.Pending,
      };
    },
    setEngagementLineData(
      state: SurveyPerformanceState,
      action: PayloadAction<{ data: SurveyPerformanceState["engagementLineData"]["value"] }>,
    ) {
      state.engagementLineData = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setSurveyQuestionsData(state: SurveyPerformanceState, action: PayloadAction<{ data: SurveyQuestion[] }>) {
      state.surveyQuestionsData = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setSelectedQuestionData(state: SurveyPerformanceState, action: PayloadAction<{ data: SurveyQuestion }>) {
      state.surveySelectedQuestionData = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setEngagementData(
      state: SurveyPerformanceState,
      action: PayloadAction<{ data: SurveyPerformanceState["engagementData"]["value"] }>,
    ) {
      state.engagementData = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setSelectedAnswerData(state: SurveyPerformanceState, action: PayloadAction<{ data: SurveyPerformanceAnswer }>) {
      state.surveySelectedAnswerData = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setPeopleDetails(state: SurveyPerformanceState, action: PayloadAction<{ data: SurveyPeopleDetails[] }>) {
      state.surveyPeopleDetails = {
        status: RStatus.Got,
        value: action.payload.data,
        errorMessage: undefined,
      };
    },
    setSurveyContentData(state, action: PayloadAction<Content>) {
      state.surveyContentData = action.payload;
    },
    setSurveyQuestionsDataError(state: SurveyPerformanceState, action: PayloadAction<{ errorMessage: string }>) {
      state.surveyQuestionsData = {
        ...state.surveyQuestionsData,
        status: RStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    setEngagementLineDataError(state: SurveyPerformanceState, action: PayloadAction<{ errorMessage: string }>) {
      state.engagementLineData = {
        ...state.engagementLineData,
        status: RStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    setSelectedQuestionDataError(state: SurveyPerformanceState, action: PayloadAction<{ errorMessage: string }>) {
      state.surveySelectedQuestionData = {
        ...state.surveySelectedQuestionData,
        status: RStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    setSelectedAnswerDataError(state: SurveyPerformanceState, action: PayloadAction<{ errorMessage: string }>) {
      state.surveySelectedAnswerData = {
        ...state.surveySelectedAnswerData,
        status: RStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    setPeopleDetailsError(state: SurveyPerformanceState, action: PayloadAction<{ errorMessage: string }>) {
      state.surveyPeopleDetails = {
        ...state.surveyPeopleDetails,
        status: RStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    setEngagementDataError(state: SurveyPerformanceState, action: PayloadAction<{ errorMessage: string }>) {
      state.engagementData = {
        ...state.engagementData,
        status: RStatus.Error,
        errorMessage: action.payload.errorMessage,
      };
    },
    reset() {
      return {
        ...initialState,
      };
    },
    resetDrilldown(state) {
      return {
        ...state,
        surveySelectedQuestionData: initialRequestState,
        surveySelectedAnswerData: initialRequestState,
      };
    },
    resetSelectedAnswer(state) {
      return {
        ...state,
        surveySelectedAnswerData: initialRequestState,
      };
    },
  },
});

// Actions
export const {
  reqSurveyQuestionsData,
  reqEngagementLineData,
  reqSelectedQuestionData,
  reqSelectedAnswerData,
  setSurveyQuestionsData,
  setEngagementLineData,
  setSelectedQuestionData,
  setSelectedAnswerData,
  setSurveyContentData,
  setSurveyQuestionsDataError,
  setEngagementLineDataError,
  setSelectedQuestionDataError,
  setSelectedAnswerDataError,
  reqPeopleDetails,
  setPeopleDetails,
  setPeopleDetailsError,
  reqEngagementData,
  setEngagementData,
  setEngagementDataError,
  reset,
  resetDrilldown,
  resetSelectedAnswer,
} = surveyPerformanceSlice.actions;

// Selectors
export const selectEngagementLineData = (state: RootState) => state.library.surveys.base.performance.engagementLineData;
export const selectSurveyQuestionsData = (state: RootState) =>
  state.library.surveys.base.performance.surveyQuestionsData;
export const selectSurveySelectedQuestionData = (state: RootState) =>
  state.library.surveys.base.performance.surveySelectedQuestionData;
export const selectSurveySelectedAnswerData = (state: RootState) =>
  state.library.surveys.base.performance.surveySelectedAnswerData;
export const selectPeopleDetails = (state: RootState) => state.library.surveys.base.performance.surveyPeopleDetails;
export const selectEngagementData = (state: RootState) => state.library.surveys.base.performance.engagementData;
export const selectSurveyContent = (state: RootState) => state.library.surveys.base.performance.surveyContentData;

// Line
export const selectEngagementLineStatus = createSelector(
  selectEngagementLineData,
  (engagementData) => engagementData.status,
);

export const selectEngagementLineDates = createSelector(selectEngagementLineData, (engagementData) =>
  engagementData.value.map((activity) => moment(activity.Date).local(true).toDate()),
);

export const selectEngagementLineCompleted = createSelector(selectEngagementLineData, (engagementData) =>
  engagementData.value.map((activity) => activity.Events.CompletedCount),
);

export const selectEngagementLineStarted = createSelector(selectEngagementLineData, (engagementData) =>
  engagementData.value.map((activity) => activity.Events.StartedCount),
);

export const selectEngagementLineCumulativeCompleted = createSelector(selectEngagementLineData, (engagementData) => {
  return engagementData.value.map((activity) => activity.Events.CumulativeCompletedCount);
});

export const selectEngagementLineCumulativeStarted = createSelector(selectEngagementLineData, (engagementData) =>
  engagementData.value.map((activity) => activity.Events.CumulativeStartedCount),
);

export const selectEngagementLineError = createSelector(
  selectEngagementLineData,
  (engagementData) => engagementData.errorMessage,
);

// Survey
export const selectSurveyQuestionsStatus = createSelector(
  selectSurveyQuestionsData,
  (surveyQuestionsData) => surveyQuestionsData.status,
);
export const selectSurveyQuestionsValue = createSelector(selectSurveyQuestionsData, (surveyQuestionsData) => {
  return surveyQuestionsData.value.map((question) => {
    if (question.QuestionType === SurveyQuestionType.RatingScale) {
      // Sort answers in descending order and exclude answer 0
      return {
        ...question,
        id: question.QuestionId,
        Answers: [...question.Answers]
          .sort((a, b) => parseInt(b.AnswerText) - parseInt(a.AnswerText))
          .filter((answer) => parseInt(answer.AnswerText) !== 0),
      };
    }
    return {
      ...question,
      id: question.QuestionId,
      Answers: question.Answers,
    };
  });
});
export const selectSurveyQuestionsError = createSelector(
  selectSurveyQuestionsData,
  (surveyQuestionsData) => surveyQuestionsData.errorMessage,
);

export const selectSurveySelectedQuestionStatus = createSelector(
  selectSurveySelectedQuestionData,
  (surveySelectedQuestionData) => surveySelectedQuestionData.status,
);
export const selectSurveySelectedQuestionValue = createSelector(
  selectSurveySelectedQuestionData,
  (surveySelectedQuestionData) => {
    if (surveySelectedQuestionData.value.QuestionType === SurveyQuestionType.RatingScale) {
      // Sort answers in descending order, exclude answer 0, and add labels to outermost answers
      return {
        ...surveySelectedQuestionData.value,
        Answers: [...surveySelectedQuestionData.value.Answers]
          .sort((a, b) => parseInt(b.AnswerText) - parseInt(a.AnswerText))
          .filter((answer) => parseInt(answer.AnswerText) !== 0)
          .map((answer) => {
            if (parseInt(answer.AnswerText) === 10) {
              return {
                ...answer,
                AnswerText: `(${surveySelectedQuestionData.value.HighestRankLabel}) ${answer.AnswerText}`,
              };
            } else if (parseInt(answer.AnswerText) === 1) {
              return {
                ...answer,
                AnswerText: `(${surveySelectedQuestionData.value.LowestRankLabel}) ${answer.AnswerText}`,
              };
            }
            return answer;
          }),
      };
    }
    return {
      ...surveySelectedQuestionData.value,
      Answers: surveySelectedQuestionData.value?.Answers,
    };
  },
);
export const selectSurveySelectedQuestionError = createSelector(
  selectSurveySelectedQuestionData,
  (surveySelectedQuestionData) => surveySelectedQuestionData.errorMessage,
);

export const selectSurveySelectedAnswerStatus = createSelector(
  selectSurveySelectedAnswerData,
  (surveySelectedAnswerData) => surveySelectedAnswerData.status,
);
export const selectSurveySelectedAnswerValue = createSelector(
  selectSurveySelectedAnswerData,
  (surveySelectedAnswerData) => surveySelectedAnswerData.value,
);
export const selectSurveySelectedAnswerError = createSelector(
  selectSurveySelectedAnswerData,
  (surveySelectedAnswerData) => surveySelectedAnswerData.errorMessage,
);
// Engagement
export const selectEngagementStatus = createSelector(selectEngagementData, (engagementData) => engagementData.status);
export const selectEngagementValue = createSelector(selectEngagementData, (engagementData) => {
  const barData = [
    {
      id: starts.toLowerCase(),
      category: starts,
      value: engagementData.value.Started,
      fillColor: reportingColors.blue,
    },
    {
      id: completes.toLowerCase(),
      category: completes,
      value: engagementData.value.Completions,
      fillColor: reportingColors.yellow,
    },
  ];

  return { barData, cardData: engagementData.value };
});
export const selectEngagementError = createSelector(
  selectEngagementData,
  (engagementData) => engagementData.errorMessage,
);

export const sortAndFilterUnique = (arr: SurveyPeopleDetails[], track = new Set()) => {
  const sortedArray = cloneDeep(arr);
  sortedArray.sort((a, b) => {
    if (a.CompletionDate === null && b.CompletionDate === null) {
      return 0;
    } else if (a.CompletionDate === null) {
      return 1;
    } else if (b.CompletionDate === null) {
      return -1;
    } else {
      return Date.parse(b.CompletionDate) - Date.parse(a.CompletionDate);
    }
  });

  return sortedArray.filter(({ UserId }) => (track.has(UserId) ? false : track.add(UserId)));
};

// People table
export const selectPeopleDetailsStatus = createSelector(selectPeopleDetails, (peopleDetails) => peopleDetails.status);
export const selectPeopleDetailsValue = createSelector(selectPeopleDetails, (peopleDetails) =>
  sortAndFilterUnique(peopleDetails.value),
);
export const selectPeopleDetailsError = createSelector(
  selectPeopleDetails,
  (peopleDetails) => peopleDetails.errorMessage,
);

export default surveyPerformanceSlice.reducer;
